const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8080/universaldrive-ws';
const API_EMAIL_SERVER = process.env.REACT_APP_EMAIL_SERVER_URL || 'http://localhost:8080/universaldrive-ws';

// API utility class for making secure HTTP requests

class ApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Makes an HTTP request with proper error handling
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers as Record<string, string>,
    };

    const config: RequestInit = {
      headers,
      credentials: 'include', // Include cookies for authentication
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('Content-Type') || '';
      const text = await response.text();

      // If not OK, try to extract a meaningful error message from JSON body
      if (!response.ok) {
        let serverMsg: string | undefined;
        try {
          if (text) {
            const looksLikeJson = contentType.includes('application/json') || /^\s*[\[{]/.test(text);
            if (looksLikeJson) {
              const json = JSON.parse(text);
              serverMsg = json?.message || json?.error || json?.msg;
            }
          }
        } catch (_) {
          // ignore JSON parse errors and fall through to raw text
        }
        // If serverMsg not found and the body is non-empty and not HTML, use raw text
        const isHtml = text && (contentType.includes('text/html') || text.startsWith('<!DOCTYPE'));
        const finalMsg = serverMsg || (!isHtml && text ? text : `HTTP error! status: ${response.status}`);
        const err = new Error(finalMsg);
        // @ts-ignore attach optional extras for callers if needed
        (err as any).status = response.status;
        throw err;
      }

      // Handle empty responses
      if (!text) {
        return {} as T;
      }

      // Check if response is HTML (redirect or error page)
      if ((contentType && contentType.includes('text/html')) || text.startsWith('<!DOCTYPE')) {
        throw new Error('Received HTML response instead of JSON. You may not be authenticated or the endpoint is misconfigured.');
      }

      return JSON.parse(text);
    } catch (error) {
      // Intercept 401: on the public site we simply surface an error; no portal redirects
      const status = (error as any)?.status;
      if (status === 401) {
        try { if (typeof window !== 'undefined') localStorage.setItem('ud_isAuthenticated', 'false'); } catch (_) { /* ignore */ }
        throw new Error('Unauthorized');
      }

      // Normalize network/connection failures to a user-friendly message
      // Common fetch network errors across browsers: TypeError: Failed to fetch, NetworkError when attempting to fetch resource,
      // net::ERR_* (surface as TypeError), and messages like "Failed to load resource: Could not connect to the server".
      let errToThrow: any = error;
      const msg = error instanceof Error ? error.message : String(error);
      const isTypeError = typeof TypeError !== 'undefined' && error instanceof TypeError;
      const looksLikeNetworkIssue =
        /Failed to fetch/i.test(msg) ||
        /NetworkError/i.test(msg) ||
        /Load failed/i.test(msg) ||
        /ERR_CONNECTION/i.test(msg) ||
        /Could not connect to the server/i.test(msg) ||
        /Failed to load resource/i.test(msg);

      if (isTypeError || looksLikeNetworkIssue) {
        errToThrow = new Error('An issue has occurred, try again later');
        // Optionally mark it so callers can differentiate if they need to
        (errToThrow as any).network = true;
      }

      // Swallow logging to avoid noisy browser console; surface via UI middleware instead
      throw errToThrow;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * Upload file request
   */
  // Removed unused uploadFile method to reduce bundle size and surface area
}

// Create and export the API service instance
export const apiService = new ApiService(API_BASE_URL);

// Export the base URL for use in other parts of the application
export { API_BASE_URL };
export { API_EMAIL_SERVER };